import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http'; // 雖然主要用 axios，但保留以防萬一或 Angular 內部需要
import { BehaviorSubject, Observable } from 'rxjs';
import axios from 'axios';
import { environment as staticEnv } from '../environments/environment'; // 您的靜態建置期環境

export interface Environment {
  clientId: string;
  companyid: string;
  production: boolean;
  title: string;
  favicon: string;
  appleTouchIcon: string;
  apiUrl: string;
  companyname: string;
  copyright: string;
  logoUrl: string;
  Login_title: string;
  Login_logoUrl: string;
  test_account: string;
  admin_account: string;
  multi_language:boolean;
  login_page_setting: {
    title_text: string;
    id_text: string;
    pw_text: string;
    login_text: string;
    forget_text: string;
  };
  css_file_name: string;
  assistantid: string;
  vector_store_id: string;
  AzureBlob: {
    blobUrl: string;
    blobsasToken: string;
    PrivateContainerName: string;
    PublicContainerName: string;
    FileUploadContainerName: string;
  };
  version: string;
  enableOnlineSupport?: boolean;
  [key: string]: any;
}

const LOCAL_STORAGE_KEY = 'app_environment_config';

@Injectable({
  providedIn: 'root'
})
export class EnvironmentService {
  public static instance: EnvironmentService; // <--- 新增靜態實例持有者

  private _environment: Environment;
  private environmentSubject: BehaviorSubject<Environment | null>; // 允許初始為 null 或預設
  public readonly environment$: Observable<Environment | null>;

  constructor(private http: HttpClient) { // HttpClient 參數保留
    EnvironmentService.instance = this; // <--- 在構造函數中賦值靜態實例
    this._environment = this.getDefaultEnvironment(); // 初始化 _environment
    this.environmentSubject = new BehaviorSubject<Environment | null>(this._environment);
    this.environment$ = this.environmentSubject.asObservable();
    console.log('EnvironmentService: Instantiated. Initial environment set.');
  }

  get environment(): Environment {
    return this._environment;
  }

  public async loadEnvironmentConfig(): Promise<void> { // Made async
    const rawIdFromHost = this.determineCompanyId(); // This is synchronous, gets initial ID (e.g., subdomain)

    console.log("rawIdFromHost",rawIdFromHost);


    // mappedCompanyId will now be the clientId fetched from /api/domain/check
    const mappedCompanyId = await this.clientIdmapping(rawIdFromHost); // rawIdFromHost is the 'domainname' for the API

    console.log("mappedCompanyId",mappedCompanyId);


    const companyIdForApi = mappedCompanyId; // This is the final clientId to use

    if (!companyIdForApi) {
        console.warn(`EnvironmentService: Could not determine companyIdForApi after checking domain/rawId '${rawIdFromHost}'. Using default environment.`);
        this._environment = this.getDefaultEnvironment();
        this.environmentSubject.next(this._environment);
        return;
    }

    // 從 staticEnv 獲取 apiUrl，確保它在服務初始化時可用
    const apiUrl = staticEnv.apiUrl || this.getDefaultEnvironment().apiUrl; // 提供回退
    const url = `${apiUrl}/api/environment/company?companyId=${encodeURIComponent(companyIdForApi)}`;

    console.log(`EnvironmentService: loadEnvironmentConfig called. Fetching from API for companyId: '${companyIdForApi}' (derived from initial host ID '${rawIdFromHost}') at ${url}`);

    return axios.get<any>(url)
      .then(response => {
        if (response.data.success && response.data.data && Array.isArray(response.data.data) && response.data.data.length > 0) {
          this._environment = response.data.data[0] as Environment;

          if(!staticEnv.production){
            this._environment.apiUrl = staticEnv.apiUrl
          }


          console.log('EnvironmentService: API data received, _environment updated:', this._environment);

          if (this._environment.companyid !== companyIdForApi) {
            console.warn(`EnvironmentService: API companyid (${this._environment.companyid}) mismatch requested (${companyIdForApi}).`);
          }

          this.environmentSubject.next(this._environment);
          console.log(`EnvironmentService: Environment config for companyId ${companyIdForApi} fetched and subject updated.`);

          try {
            localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(this._environment));
            console.log(`EnvironmentService: Environment config saved to localStorage.`);
          } catch (e) {
            console.error('EnvironmentService: Error saving environment to localStorage.', e);
          }

        } else {
          console.warn(`EnvironmentService: API response for ${companyIdForApi} not as expected. Using default environment.`);
          this._environment = this.getDefaultEnvironment();
          this.environmentSubject.next(this._environment);
        }
      })
      .catch(error => {
        console.error(`EnvironmentService: Failed to load config for ${companyIdForApi} from API. Using default. Error:`, error);
        this._environment = this.getDefaultEnvironment();
        this.environmentSubject.next(this._environment);
      });
  }

  private determineCompanyId(): string { // This remains synchronous
    if (typeof window !== 'undefined') {
      const hostname = window.location.hostname.toLowerCase();

        // 特定 Azure Static Web App 網址的處理
    if (hostname.includes('dr-esh-website') && hostname.includes('azurewebsites.net')) {
      console.log('EnvironmentService: Detected Dr.ESH Azure Static Web App URL, returning I&D as companyId');
      return 'IAD';
    }


      const parts = hostname.split('.');
      // This logic extracts the part of the hostname that will be used as 'domainname' for the API call
      if (hostname.endsWith('.localhost')) {
        if (parts.length === 3 && parts[0] === 'www' && parts[1] !== 'localhost' && parts[1] !== '') {
          return parts[1]; // e.g., 'client1' from 'www.client1.localhost'
        } else if (parts.length === 2 && parts[0] !== 'localhost' && parts[0] !== '') {
          return parts[0]; // e.g., 'client1' from 'client1.localhost'
        }
      } else if (parts.length > 2) {
        if (parts[0] !== 'www') {
          return parts[0]; // e.g., 'client1' from 'client1.example.com'
        }
        // Potentially return parts[1] for 'www.client1.example.com' if needed
      }

    }
    // Fallback if not determined from subdomain or if window is undefined
    return staticEnv.clientId || 'default_client'; // This acts as a fallback "domainname" if parsing fails
  }

  // idToMap is the domainname (e.g., extracted subdomain or full hostname)
  private async clientIdmapping(idToMap: string): Promise<string> {


    console.log(`EnvironmentService: clientIdmapping called with idToMap: '${idToMap}'`);

    const domainname = idToMap; // idToMap is the domainname for the API call

    if (!domainname || domainname === 'default_client') { // Check if we have a valid domainname from determineCompanyId
        console.warn(`EnvironmentService: Invalid domainname ('${domainname}') provided for clientIdmapping. Cannot call API. Returning empty clientId.`);
        return ''; // Fallback to empty string, loadEnvironmentConfig will handle this
    }

    const checkApiUrlBase = staticEnv.apiUrl || this.getDefaultEnvironment().apiUrl;
    const checkUrl = `${checkApiUrlBase}/api/domain/check?domain=${encodeURIComponent(domainname)}`;
    console.log(`EnvironmentService: Calling domain check API for clientId mapping: ${checkUrl}`);

    try {
      const response = await axios.get<{ success: boolean, clientId: string, requestId?: string }>(checkUrl);
      if (response.data && response.data.success && typeof response.data.clientId === 'string') {
        console.log(`EnvironmentService: Domain check API success. Domain: ${domainname}, Resolved ClientId: ${response.data.clientId}, RequestId: ${response.data.requestId}`);
        return response.data.clientId; // This is the actual clientId from the API
      } else {
        console.warn(`EnvironmentService: Domain check API call not successful or data malformed for domain ${domainname}. Response:`, response.data);
        return ''; // Fallback to empty string if API doesn't provide a valid clientId
      }
    } catch (error) {
      console.error(`EnvironmentService: Error calling domain check API for domain ${domainname}. Error:`, error);
      return ''; // Fallback to empty string on error
    }
  }

  public getDefaultEnvironment(): Environment {
    return {
      clientId: staticEnv.clientId || 'default_client',
      companyid: '',
      production: false,
      title: 'Default Application Title',
      favicon: 'assets/default-favicon.ico',
      appleTouchIcon: 'assets/default-apple-touch-icon.png',
      apiUrl: staticEnv.apiUrl || '/api', // 使用建置期 apiUrl 作為預設
      companyname: 'Default Company',
      copyright: `© ${new Date().getFullYear()} Default Company`,
      logoUrl: 'assets/default-logo.png',
      Login_title: '<h1>Login</h1>',
      Login_logoUrl: 'assets/default-login-logo.png',
      test_account: 'test',
      admin_account: 'admin',
      multi_language: false, // 默認不啟用多語言
      login_page_setting: {
        title_text: "Welcome",
        id_text: "ID",
        pw_text: "Password",
        login_text: "Login",
        forget_text: "Forgot Password?"
      },
      css_file_name: 'default.css',
      assistantid: '',
      vector_store_id: '',
      AzureBlob: {
        blobUrl: '',
        blobsasToken: '',
        PrivateContainerName: '',
        PublicContainerName: '',
        FileUploadContainerName: ''
      },
      version: '0.0.0',
      enableOnlineSupport: true
    };
  }

  public clearStoredEnvironment(): void {
    localStorage.removeItem(LOCAL_STORAGE_KEY);
    this._environment = this.getDefaultEnvironment();
    this.environmentSubject.next(this._environment);
    console.log('EnvironmentService: Stored environment config cleared from localStorage.');
  }
}
